/**
 * Custom error classes for better error handling
 */

/**
 * AuthError - Thrown when authentication fails (401/403)
 * This error should be handled gracefully without console noise
 */
export class AuthError extends Error {
  statusCode: number;

  constructor(message: string, statusCode: number = 401) {
    super(message);
    this.name = "AuthError";
    this.statusCode = statusCode;

    // Maintains proper stack trace for where our error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AuthError);
    }
  }
}

/**
 * NetworkError - Thrown when network request fails
 */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NetworkError";

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NetworkError);
    }
  }
}

/**
 * APIError - Generic API error for non-auth issues
 */
export class APIError extends Error {
  statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = "APIError";
    this.statusCode = statusCode;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, APIError);
    }
  }
}

/**
 * PageAccessGuard's raw message (e.g. `Access denied: requires can_edit on the "sales" page`)
 * is meant for logs/API consumers, not end users — it names an internal relation they've never
 * heard of. Both api-client.ts's typed client and utils.ts's untyped `fetcher` build their own
 * error message from the response body, so this lives here, shared, rather than forking a
 * third copy the next time another fetch path needs it.
 */
const PAGE_ACCESS_DENIED = /^Access denied: requires (can_read|can_edit) on the "[\w-]+" page$/;

export function readableErrorMessage(raw: string): string {
  const match = PAGE_ACCESS_DENIED.exec(raw);
  if (!match) return raw;
  return match[1] === "can_edit"
    ? "You have read-only access, so this action isn't available."
    : "You don't have access to this section.";
}

/**
 * Check if an error is an authentication error
 */
export function isAuthError(error: unknown): error is AuthError {
  return error instanceof AuthError;
}

/**
 * Check if an error is a network error
 */
export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

/**
 * Check if an error is an API error
 */
export function isAPIError(error: unknown): error is APIError {
  return error instanceof APIError;
}

/** The error's own message, or `fallback` when it isn't an Error. */
export function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}
